import { useEffect, useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { 
  useAuthStore, 
  MASTER_LOGIN_URL,
  getAuthCookie,
  getOrgCookie,
} from '@quotech/auth'
import { dashboardApi, inspectionApi } from '@/lib/api'
import { useQaAppCapabilities } from '@/lib/useQaAppCapabilities'
import {
  FileText,
  CheckCircle,
  Clock,
  AlertTriangle,
  Plus,
  ArrowRight,
  ClipboardList,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Building2,
  Calendar,
  RefreshCw,
  Loader2,
  LogIn,
} from 'lucide-react'
import { cn, useIsMobile } from '@/lib/utils'
import { InspectionRecordLink } from './inspectionRecordLink'

export default function DashboardPage() {
  const { canCreateQaJob } = useQaAppCapabilities()
  // Auth state - use Zustand store (synced with cookies via SessionManager)
  const storeOrgId = useAuthStore((s) => s.organisationId)
  const storeToken = useAuthStore((s) => s.accessToken)
  const checkAuth = useAuthStore((s) => s.checkAuth)
  
  // CRITICAL FIX: Read cookies synchronously via useMemo (not useEffect)
  // This ensures isAuthReady is correct on FIRST render, enabling queries immediately.
  // The pattern matches offline.ts which works correctly.
  const { accessToken, organisationId } = useMemo(() => {
    const cookieToken = getAuthCookie()
    const cookieOrg = getOrgCookie()
    return {
      accessToken: cookieToken || storeToken,
      organisationId: cookieOrg?.id || storeOrgId,
    }
  }, [storeOrgId, storeToken])
  
  // Local state for auth checking (only used when org is missing)
  const [authCheckAttempted, setAuthCheckAttempted] = useState(false)
  const [isCheckingAuth, setIsCheckingAuth] = useState(false)
  const isMobile = useIsMobile()
  
  // Try to fetch org context if we have token but no org
  useEffect(() => {
    const tryFetchOrg = async () => {
      if (accessToken && !organisationId && !authCheckAttempted && !isCheckingAuth) {
        setIsCheckingAuth(true)
        try {
          await checkAuth()
        } catch (e) {
          if (import.meta.env.DEV) console.error('[QA Dashboard] checkAuth failed:', e)
        } finally {
          setIsCheckingAuth(false)
          setAuthCheckAttempted(true)
        }
      }
    }
    tryFetchOrg()
  }, [accessToken, organisationId, authCheckAttempted, isCheckingAuth, checkAuth])
  
  const isAuthReady = !!organisationId
  const hasNoOrg = authCheckAttempted && !organisationId && !!accessToken

  const { data: stats, isLoading: statsLoading, error: statsError, refetch: refetchStats } = useQuery({
    queryKey: ['qa-dashboard-stats', organisationId],
    queryFn: () => dashboardApi.getDashboardStats(),
    enabled: isAuthReady,
    retry: 2,
  })

  const { data: recentInspections, isLoading: inspectionsLoading, error: inspectionsError } = useQuery({
    queryKey: ['qa-recent-inspections', organisationId],
    queryFn: () => inspectionApi.getInspections({ page_size: 5 }),
    enabled: isAuthReady,
    retry: 2,
  })
  
  // Loading while checking auth
  const isLoadingAuth = !isAuthReady && !authCheckAttempted
  const hasError = !!statsError || !!inspectionsError
  
  // If we have token but still no org after check, show re-login prompt
  if (hasNoOrg) {
    const returnUrl = encodeURIComponent(window.location.href)
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-amber-100 dark:bg-amber-900/30 mx-auto mb-4 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Organisation Not Found</h2>
          <p className="text-muted-foreground mb-6">
            Your session doesn't have an organisation context. This usually happens when cookies expire. Please log in again.
          </p>
          <a
            href={`${MASTER_LOGIN_URL}?returnTo=${returnUrl}&reason=no_org`}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 transition-colors"
          >
            <LogIn className="w-5 h-5" />
            Log In Again
          </a>
        </div>
      </div>
    )
  }
  
  // Show loading while checking auth
  if (isCheckingAuth) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">Loading organisation...</p>
        </div>
      </div>
    )
  }

  const statCards = [
    {
      title: 'Total Inspections',
      value: stats?.total_inspections ?? 0,
      icon: ClipboardList,
      iconColor: 'text-primary-600 dark:text-primary-400',
      bgColor: 'bg-primary-50 dark:bg-primary-900/20',
      trend: 'up',
    },
    {
      title: 'In Progress',
      value: stats?.in_progress ?? 0,
      icon: Clock,
      iconColor: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-50 dark:bg-amber-900/20',
      trend: 'neutral',
    },
    {
      title: 'Ready for Pack',
      value: stats?.ready_for_pack ?? 0,
      icon: CheckCircle,
      iconColor: 'text-emerald-600 dark:text-emerald-400',
      bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
      trend: 'neutral',
    },
    {
      title: 'Open NCRs',
      value: stats?.ncrs_open ?? 0,
      icon: AlertTriangle,
      iconColor: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      trend: (stats?.ncrs_open ?? 0) > 0 ? 'down' : 'neutral',
    },
  ]

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      draft: 'bg-muted text-muted-foreground',
      published: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      ready_for_pack: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      submitted: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
      archived: 'bg-muted text-muted-foreground',
    }
    return styles[status] || styles.draft
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">QA Dashboard</h1>
          <p className="text-muted-foreground">Overview of quality assurance activities</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => refetchStats()}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          {canCreateQaJob ? (
          <Link
            to="/records/new"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 dark:hover:bg-primary-400 shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30"
          >
            <Plus className="w-4 h-4" />
            New Inspection
          </Link>
          ) : null}
        </div>
      </div>

      {/* Error Banner */}
      {hasError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-red-800 dark:text-red-200">Failed to load dashboard data</p>
            <p className="text-sm text-red-600 dark:text-red-400 mt-1">
              {(statsError as Error)?.message || (inspectionsError as Error)?.message || 'Please check your connection and try again.'}
            </p>
          </div>
          <button
            onClick={() => refetchStats()}
            className="px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div
            key={card.title}
            className="bg-card rounded-xl shadow-sm border border-border p-5 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={cn('w-11 h-11 rounded-xl flex items-center justify-center', card.bgColor)}>
                <card.icon className={cn('w-5 h-5', card.iconColor)} />
              </div>
              {card.trend === 'up' && <TrendingUp className="w-4 h-4 text-emerald-500" />}
              {card.trend === 'down' && <TrendingDown className="w-4 h-4 text-red-500" />}
            </div>
            <div>
              <p className="text-3xl font-bold text-foreground tracking-tight">
                {statsLoading || isLoadingAuth ? (
                  <span className="inline-block w-12 h-8 bg-muted rounded animate-pulse" />
                ) : (
                  card.value.toLocaleString()
                )}
              </p>
              <p className="text-sm font-medium text-muted-foreground mt-1">{card.title}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity and Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Inspections */}
        <div className="lg:col-span-2 bg-card rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="font-semibold text-foreground flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              Recent Inspections
            </h2>
            <Link
              to="/records"
              className="text-sm text-primary hover:text-primary-600 dark:hover:text-primary-400 font-medium flex items-center gap-1 transition-colors"
            >
              View all
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-border">
            {inspectionsLoading || isLoadingAuth ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-muted rounded-lg animate-pulse" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-2/3 bg-muted rounded animate-pulse" />
                      <div className="h-3 w-1/3 bg-muted rounded animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : Array.isArray(recentInspections?.results) && recentInspections.results.length ? (
              recentInspections.results.slice(0, 5).map((inspection: Record<string, unknown>) => (
                <InspectionRecordLink
                  key={String(inspection.id)}
                  mobile={isMobile}
                  inspectionId={String(inspection.id)}
                  jobId={typeof inspection.job_id === 'string' || typeof inspection.job_id === 'number' ? inspection.job_id : null}
                  className="flex items-center justify-between p-4 hover:bg-accent/50 transition-colors group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 group-hover:bg-primary/10 transition-colors">
                      <FileText className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground text-sm truncate group-hover:text-primary transition-colors">
                        {inspection.inspection_name || 'Untitled Inspection'}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                        {(inspection.job_id || inspection.job_name) && (
                          <>
                            <Building2 className="w-3 h-3" />
                            <span className="truncate">
                              {inspection.job_id ? `J${inspection.job_id}` : inspection.job_name}
                            </span>
                          </>
                        )}
                        {inspection.created_at && (
                          <span className="text-muted-foreground/70">
                            · {new Date(inspection.created_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <span className={cn(
                    'text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ml-3',
                    getStatusBadge(inspection.status)
                  )}>
                    {(inspection.status || 'draft').replace(/_/g, ' ')}
                  </span>
                </InspectionRecordLink>
              ))
            ) : (
              <div className="p-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-muted mx-auto mb-4 flex items-center justify-center">
                  <FileText className="w-8 h-8 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-foreground mb-1">No inspections yet</p>
                <p className="text-xs text-muted-foreground mb-4">Create your first QA inspection to get started</p>
                {canCreateQaJob ? (
                <Link
                  to="/records/new"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary-600 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  New Inspection
                </Link>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-card rounded-xl shadow-sm border border-border p-5">
          <h2 className="font-semibold text-foreground mb-4">Quick Actions</h2>
          <div className="space-y-3">
            {canCreateQaJob ? (
            <Link
              to="/records/new"
              className="flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-accent hover:border-primary/30 transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Plus className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground text-sm group-hover:text-primary transition-colors">New Inspection</p>
                <p className="text-xs text-muted-foreground">Start a new QA inspection</p>
              </div>
            </Link>
            ) : null}
            <Link
              to="/templates"
              className="flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-accent hover:border-primary/30 transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                <ClipboardList className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="font-medium text-foreground text-sm group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">Templates</p>
                <p className="text-xs text-muted-foreground">Manage checklist templates</p>
              </div>
            </Link>
            <Link
              to="/ncrs"
              className="flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-accent hover:border-primary/30 transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="font-medium text-foreground text-sm group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">NCRs</p>
                <p className="text-xs text-muted-foreground">Non-conformance records</p>
              </div>
            </Link>
            <Link
              to="/reports"
              className="flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-accent hover:border-primary/30 transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                <BarChart3 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="font-medium text-foreground text-sm group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">Reports</p>
                <p className="text-xs text-muted-foreground">View QA reports and packs</p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
